import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Department, FilterGroupingEnum } from 'graphql-common';
import Table from '@lib/components/Table/Table';
import Modal from '@lib/components/Modal/Modal';
import { ModalMode } from '@lib/components/Modal/enums';
import Search from '@lib/components/Search/Search';
import useSearchParam from '@lib/hooks/useSearchParam';
import {
  ORDERING_PARAMS,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import { APP_URLS } from 'constants/urls';
import useDepartmentsQueryHook from 'utils/fetch/hooks/useDepartmentsQueryHook';
import getDepartmentsTableColumns from 'routes/Departments/utils/getDepartmentsTableColumns';
import useDepartmentsTableTree from 'routes/Departments/utils/useDepartmentsTableTree';

function InterventionDepartments({
  interventionId,
  hasLinkedDepartments,
}: {
  interventionId?: string;
  hasLinkedDepartments: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery] = useSearchParam();
  const paginationState = useState<PaginationParams>(PAGINATION_PARAMS);
  const orderingState = useState<OrderingParams>(ORDERING_PARAMS);
  const [paginationParams] = paginationState;
  const [orderingParams] = orderingState;

  const { collection, loading, totalCount } = useDepartmentsQueryHook({
    searchQuery,
    paginationParams,
    orderingParams,
    additionalFilters:
      hasLinkedDepartments && interventionId
        ? [
            {
              departmentInterventionsInterventionId: {
                grouping: FilterGroupingEnum.And,
                predicate: { eq: interventionId },
              },
            },
          ]
        : undefined,
  });

  const onView = (department: Department) => {
    if (department?.id) {
      navigate(
        APP_URLS.dashboard.departments.view.getDynamicPath({
          pathParts: { id: department.id },
        }),
      );
    }
  };

  const tableColumns = getDepartmentsTableColumns({ onView, t });
  const departmentsTableTree = useDepartmentsTableTree({
    collection,
    searchQuery,
  });

  return (
    <>
      <Search placeholder={t('departments-search-placeholder')} />
      <Table
        counterText={t('department-plural', { count: totalCount })}
        columns={tableColumns}
        data={hasLinkedDepartments ? collection : departmentsTableTree.data}
        id="intervention-departments"
        loading={loading}
        paginationState={paginationState}
        orderingState={orderingState}
        totalEntries={totalCount}
        tree={hasLinkedDepartments ? undefined : departmentsTableTree.tree}
      />
    </>
  );
}

type Props = {
  hasLinkedDepartments: boolean;
  interventionId?: string;
  isOpen: boolean;
  onClose: () => void;
};

function InterventionDepartmentsModal({
  hasLinkedDepartments,
  interventionId,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      caption={t('linked-departments')}
      mode={ModalMode.FullScreen}
    >
      {isOpen ? (
        <InterventionDepartments
          interventionId={interventionId}
          hasLinkedDepartments={hasLinkedDepartments}
        />
      ) : (
        <div />
      )}
    </Modal>
  );
}

export default memo(InterventionDepartmentsModal);
